import React, { useEffect, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { paths } from 'routes';

import { Search } from 'components/Search';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { PremiumBanner } from '../../subscriptions/components/PremiumBanner';

import { useDirects } from '../api/getAll';
import { useDirectsCount } from '../api/getCount';
import { DirectListItem } from '../components/ListItem';
import { Add } from '../components/Add';
import { useAuth } from 'lib/auth';
import { CheckboxFilter } from 'components/Filter/Inputs/CheckboxFilter';
import { OrderByDropdown } from 'components/Filter/Inputs/OrderByDropdown';
import { Loader } from 'components/Loader';
import { YellowMessageBar } from 'components/YellowMessageBar';
import { AdditionalFiltersDropdown } from 'components/Filter/Inputs/AdditionalFilters';

export const List = () => {
  const { account, user } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    includeInactive: false,
    acceptingOffers: false,
    sortBy: !account.super_admin && user.Company.subscription_level === 'free' ? 'dateCreated' : sessionStorage.getItem('direct_sortBy') || 'dateCreated',
    additionalFilters: sessionStorage.getItem('direct_additionalFilters') || 'notSet',
  });
  const [loading, setLoading] = useState(false);

  const [additionalDirect, setAdditionalDirect] = useState(0);
  const [timer, setTimer] = useState(null);

  const {
    data: { directs, total },
    refetch: refetchDirects,
    isFetching: isFetching1,
  } = useDirects({
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      }
    },
  });

  const {
    //@ts-ignore
    data: { count: directsCount },
    isFetching: isFetching2,
  } = useDirectsCount();

  const {
    //@ts-ignore
    data: { count: expiredDirectsCount },
    isFetching: isFetching3,
  } = useDirectsCount(true);

  const isFetching = isFetching1 || isFetching2 || isFetching3;

  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  const handleFilterUpdate = ({ target: { name, value, checked } }) => {
    setFilters((filters) => {
      switch (name) {
        case 'sortBy':
          sessionStorage.setItem('direct_sortBy', value);
          return { ...filters, [name]: value };
        case 'additionalFilters':
          sessionStorage.setItem('direct_additionalFilters', value);
          return { ...filters, [name]: value };

        default:
          return { ...filters, [name]: checked };
      }
    });
  };

  const handlePageChange = (_, page) => setPage(page);

  const caption = account.super_admin ? 'Click the "Add Direct" button to begin' : '';

  useDirectsRefetch();

  useEffect(() => {
    setAdditionalDirect(directsCount + expiredDirectsCount - total);
  }, [total, directsCount, expiredDirectsCount])

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  return (
    <ContentLayout>
      <Stack height={1}>
        <Stack>
          <BreadCrumbs
            items={[
              { name: '', to: paths.directs },
              { name: `Directs${total > 0 ? ` (${total})` : ''}` },
            ]}
          />
        </Stack>
        <Stack>
          
        </Stack>
        {!account.super_admin && (
          <>
          {(!user.Company.is_premium || user.Company.subscription_level === 'free') && (
            <PremiumBanner sx={{my: 0}} message={`There are ${additionalDirect} more directs available for Premium members. Click here for more information`} handleSuccess={refetchDirects} currentLevel={user.Company.subscription_level} />
          )}
          </>
        )}

        <Stack
          px={4}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          height="100%"
        >
          <Stack direction="row" gap={2} alignItems="center">
            <Search handleSearchChange={handleSearchChange} sx={{ minWidth: 200, width: 270 }} />
            <Stack direction="row" gap={2} sx={{ flex: '1' }}>
              <CheckboxFilter
                adminOnly
                name="includeInactive"
                onChange={handleFilterUpdate}
                checked={filters.includeInactive}
                label="Inactive"
              />
              <CheckboxFilter
                name="acceptingOffers"
                onChange={handleFilterUpdate}
                checked={filters.acceptingOffers}
                label={`Open only (${directsCount})`}
                subscriptionTypes={['silver', 'gold']}
                tooltip="Premium feature"
              />
            </Stack>
            <AdditionalFiltersDropdown
              adminOnly
              name="additionalFilters"
              onChange={handleFilterUpdate}
              selected={filters.additionalFilters}
              options={[
                { value: 'notSet', text: 'Please select' },
                { value: 'missingBidders', text: 'Missing blacklist' },
                { value: 'missingGeneralFiles', text: 'Missing general files' },
                { value: 'missingBiddersFiles', text: 'Missing blacklist files' },
                { value: 'missingAgreement', text: 'Missing agreement' },
              ]}
            />
            <OrderByDropdown
              name="sortBy"
              onChange={handleFilterUpdate}
              selected={filters.sortBy}
              options={[
                { value: 'dateCreated', text: 'Date Created' },
                {
                  value: 'expiringDate',
                  text: 'Expiring Date',
                  subscriptionType: ['silver', 'gold'],
                  tooltip: 'premium feature',
                },
                {
                  value: 'postingDate',
                  text: 'Posting Date',
                  subscriptionType: ['silver', 'gold'],
                  tooltip: 'premium feature',
                },
              ]}
            />
            {account.super_admin && (
              <Add refetchDirects={refetchDirects} />
            )}
          </Stack>

          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {loading && (
              <Loader />
            )}

            {!loading && (
            <>
            {!directs && (
              <Stack
                height="100%"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5">No Directs Added</Typography>
                {account.super_admin && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      letterSpacing: '-0.14px',
                    }}
                  >
                    Click the "Add Direct" button to begin
                  </Typography>
                )}
              </Stack>
            )}

            {directs && (
              <>
              <Stack
                flex={1}
                mt={2}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                gap={1}
              >
                {directs.length === 0 && (
                  <NotFound
                    wrapperSx={{ mt: 19 }}
                    title="No directs found"
                    caption={caption}
                  />
                )}
                {directs.length !== 0 && (
                  <>
                  <YellowMessageBar message={"Delegon offers a direct archive, not a direct monitoring service."} sx={{height: 40, mb: 1}} />
                  {directs.map((item) => (
                    <DirectListItem key={item.direct_id} {...item} />
                  ))}
                  </>
                )}
              </Stack>
              </>
            )}

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
          </>
            )}
            </>
          ) :
          (
            <></>
          )}
        </Stack>
      </Stack>
    </ContentLayout>
  );

  function useDirectsRefetch() {
    useEffect(() => {
      refetchDirects();
    }, [page, pageSize, filters, search]);
  }
};
